import { extend, detectWidgetClick } from "@dhx/ts-common/core";
import { create, el, VNode } from "@dhx/ts-common/dom";
import { addHotkeys } from "@dhx/ts-common/Keymanager";
import { Selection, ISelection } from "@dhx/ts-list";
import { View } from "@dhx/ts-common/view";
import { DataCollection, DataEvents, DragEvents, dragManager, IDataEventsHandlersMap, IDragEventsHandlersMap } from "@dhx/ts-data";
import { EventSystem, IEventSystem } from "@dhx/ts-common/events";
import {isIE, locate, locateNode} from "@dhx/ts-common/html";
import { IHandlers, SelectionEvents } from "@dhx/ts-common/types";
import { IDataViewConfig, DataViewEvents, IDataViewEventHandlersMap, IDataView } from "./types";
import { getEditor } from "./editors/editors";

export class DataView extends View implements IDataView {
	public config: IDataViewConfig;
	public data: DataCollection;
	public events: IEventSystem<DataEvents | DragEvents | DataViewEvents, IDataEventsHandlersMap & IDragEventsHandlersMap & IDataViewEventHandlersMap>;
	public selection: ISelection;

	private _handlers: IHandlers;
	private _navigationDestructor: () => void;
	private _documentClickDestuctor: () => void;
	private _focusIndex: number;
	private _widgetInFocus: boolean;
	private _edited: string;

	constructor(node: HTMLElement|string, config: IDataViewConfig = {}){
		super(node, extend({
			keyNavigation: true,
			itemsInRow: 1,
			multiselectionMode: config.multiselectionMode ? config.multiselectionMode : "click",
			gap: "0px",
			editing: false
		}, config));

		if (Array.isArray(this.config.data)) {
			this.events = new EventSystem<DataEvents>(this);
			this.data = new DataCollection({}, this.events);
			this.data.parse(this.config.data);
		} else if (this.config.data && this.config.data.events) {
			this.data = this.config.data;
			this.events = this.data.events;
			this.events.context = this;
		} else {
			this.events = new EventSystem<DataEvents>(this);
			this.data = new DataCollection({}, this.events);
		}
		this.selection = new Selection({
			multiselection: this.config.multiselection,
			multiselectionMode: this.config.multiselectionMode,
		}, this.data);

		this._getHotkeys();

		const updater = (updateObj: any) => (id: string, ids?: any) => {
			if (ids && ids instanceof Array) {
				ids.map(selectedId => this.data.exists(selectedId) && this.data.update(selectedId, updateObj));
				return;
			}
			if (this.data.exists(id)) {
				this.data.update(id, updateObj);
			}
		};
		this.events.on(DataEvents.change, () => {
			this.paint();
		});
		this.events.on(DragEvents.canDrop, updater({$drophere: true}));
		this.events.on(DragEvents.cancelDrop, updater({$drophere: undefined}));
		this.events.on(DragEvents.dragStart, updater({$dragtarget: true}));
		this.events.on(DragEvents.dragEnd, updater({$dragtarget: undefined}));
		this.events.on(DataViewEvents.afterEditEnd, (value, id) => {
			const item = this.data.getItem(id);
			this.data.update(id, {...item, value});
			this._edited = null;
			this._getHotkeys();
			this.paint();
		});
		this.selection.events.on(SelectionEvents.afterSelect, id => {
			this.setFocus(id);
		});
		this._handlers = {
			onmousedown: (e: MouseEvent) => {
				const itemsForGhost = [];
				const item = locateNode(e, "dhx_id") as HTMLElement;
				const itemId = item && item.getAttribute("dhx_id");
				const selectionIds =  this.selection.getId();
				if (this.config.multiselection && selectionIds instanceof Array) {
					selectionIds.map(id => {
						if (id !== itemId) {
							itemsForGhost.push(this.getRootView().refs[id].el);
						}
					});
				}
				return this.config.dragMode && !this._edited ? dragManager.onMouseDown(e, this.selection.getId(), itemsForGhost) : null;
			},
			ondragstart: () => this.config.dragMode && !this._edited ? false : null,
			oncontextmenu: (e: MouseEvent) => {
				const id = locate(e);
				if (!id) {
					return;
				}
				this.events.fire(DataViewEvents.contextmenu, [id, e]);
			},
			ondblclick: (e: MouseEvent) => {
				const id = locate(e);
				if (!id) {
					return;
				}
				if (this.config.editing) {
					this.edit(id);
				}
				this.events.fire(DataViewEvents.doubleClick, [id, e]);
			},
			onclick: (e: MouseEvent) => {
				const id = locate(e);
				if (!id) {
					return;
				}
				this.setFocus(id);
				this.selection.add(id, e.ctrlKey || e.metaKey, e.shiftKey);
				this.events.fire(DataViewEvents.click, [id, e]);
			},
		};
		if (this.config.dragMode) {
			dragManager.setItem(this._uid, this);
		}

		const view =  create({
			render: () => this._draw(),
			hooks: {
				didRedraw: (vm) => {
					const rootEl = vm.node.el;
					const hasScroll = rootEl.scrollHeight > rootEl.offsetHeight;
					const classAttr: string = vm.node.attrs.class.replace(" dhx_dataview--has-scroll", "");
					const newClassName = hasScroll ? classAttr + " dhx_dataview--has-scroll" : classAttr;
					vm.node.patch({class: newClassName});
				}
			}
		});
		this.mount(node, view);
	}
	edit(id: string): void {
		this._edited = id;
		if (!this.data.getItem(this._edited) || !this.events.fire(DataViewEvents.beforeEditStart, [id])) {
			this._edited = null;
			return;
		}
		this._getHotkeys();
		this.paint();
		this.events.fire(DataViewEvents.afterEditStart, [id]);
	}
	getFocusItem() {
		return this.data.getItem(this.data.getId(this._focusIndex));
	}
	setItemInRow(amount) {
		this.config.itemsInRow = amount;
		this.paint();
	}
	setFocus(id: string): void {
		const index = this.data.getIndex(id);
		this._setFocusIndex(index);
	}
	getFocus(): string {
		const item = this.data.getItem(this.data.getId(this._focusIndex));
		if (item) {
			return item.id;
		}
	}
	destructor() {
		this.events.clear();
		if (this._navigationDestructor) {
			this._navigationDestructor();
		}
		if (this._documentClickDestuctor) {
			this._documentClickDestuctor();
		}
		this.unmount();
	}
	// TODO: remove sute_7.0
	getFocusIndex() {
		return this._focusIndex;
	}
	// TODO: remove sute_7.0
	setFocusIndex(index: number): void {
		this._setFocusIndex(index);
	}
	private _setFocusIndex(index: number): void {
		if (index < 0 || index > this.data.getLength() - 1) {
			return;
		}
		this._focusIndex = index;

		const node = this.getRootNode();
		if (!node || !node.parentNode) {
			return;
		}
		const itemRow = node.children[Math.floor(this._focusIndex / this.config.itemsInRow)] as HTMLElement;
		if (itemRow) {
			const item = itemRow.children[this._focusIndex % this.config.itemsInRow] as HTMLElement;
			const gap = parseInt(this.config.gap.toString().replace("px", ""), null);
			if (item.offsetTop >= node.clientHeight + node.scrollTop - item.clientHeight) {
				node.scrollTop = item.offsetTop - node.clientHeight + item.clientHeight + gap;
			} else if (item.offsetTop < node.scrollTop - gap) {
				node.scrollTop = item.offsetTop - gap;
			}
		}
		this.events.fire(DataViewEvents.focusChange, [this._focusIndex, this.data.getId(this._focusIndex)]);
		this.paint();
		return;
	}
	private _renderItem(item: any, focus: boolean, isLastItemInRow: boolean) : VNode {
		const { itemsInRow, gap, template, itemHeight } = this.config;
		const html = template ? template(item) : item.htmlContent;
		const gapWithPx = (gapSize: any) => parseFloat(gapSize);
		if (item.id === this._edited) {
			const editor = getEditor(item, this);
			return editor.toHTML(isLastItemInRow);
		}
		return el("div", {
			// tabindex: "1",
			class: "dhx_dataview-item" +
			(item.$selected ? " dhx_dataview-item--selected" : "") +
			(focus ? " dhx_dataview-item--focus" : "") +
			(item.$drophere && !this._edited ? " dhx_dataview-item--drophere" : "") +
			(item.$dragtarget && !this._edited ? " dhx_dataview-item--dragtarget" : "") +
			(this.config.dragMode && !this._edited ? " dhx_dataview-item--drag" : "") +
			(gapWithPx(gap) ? " dhx_dataview-item--with-gap": "") +
			(item.css && item.$selected || item.css && focus ? "" : item.css ? " " + item.css : "") +
			(isLastItemInRow ? " dhx_dataview-item--last-item-in-row" : ""),
			style: {
				"width": `calc(${100/itemsInRow}% - ${gapWithPx(gap)} * ${(itemsInRow - 1) / itemsInRow}px)`,
				"margin-right": isLastItemInRow ? "" : gap,
				"height": template ? null : itemHeight
			},
			_key: item.id,
			dhx_id: item.id,
			_ref: item.id,
		},
		html ? [ el(".dhx_dataview-item__inner-html", {
			".innerHTML": html
		})] : item.value || item.text || item.value);
	}
	private _draw() : VNode {
		const { itemsInRow, css, gap } = this.config;
		let currentCounter = 0;
		const rows = this.data.reduce((items, obj, index) => {
			if(currentCounter === 0) {
				items.push([]);
			}
			items[items.length - 1].push(this._renderItem(obj, index === this._focusIndex, currentCounter === itemsInRow - 1));
			currentCounter = (currentCounter + 1) % itemsInRow;
			return items;
		}, []);
		return el ("", {
			...this._handlers,
			dhx_widget_id: this._uid,
			class: (css ? css : "") + " dhx_widget dhx_dataview" +
			(this.config.multiselection && this.selection.getItem() ? " dhx_no-select--pointer" : ""),
			style: {
				height: this.config.height
			}
		}, rows.map(row => el(".dhx_dataview-row", {
			style: {margin: gap},
		},  row)));
	}
	private _getHotkeys(): void {
		if (this.config.keyNavigation) {
			if (this._edited) {
				if (this._navigationDestructor) {
					this._navigationDestructor();
				}
			} else {
				let keyNavigation = this.config.keyNavigation as any;
				if (typeof this.config.keyNavigation !== "function") {
					this._widgetInFocus = false;
					keyNavigation = () => this._widgetInFocus;
					this._documentClickDestuctor = detectWidgetClick(this._uid, isInnerClick => this._widgetInFocus = isInnerClick);
				}
				const preventEvent = (fn: () => void) => (e: Event) => {
					e.preventDefault();
					fn();
				};
				let handlers:any = {
					"arrowDown": preventEvent(() => this.setFocusIndex(this._focusIndex + this.config.itemsInRow)),
					"arrowUp": preventEvent(() => this.setFocusIndex(this._focusIndex - this.config.itemsInRow)),
					"arrowLeft": preventEvent(() => this.setFocusIndex(this._focusIndex - 1)),
					"arrowRight": preventEvent(() => this.setFocusIndex(this._focusIndex + 1)),
					"enter": (e: KeyboardEvent) => {
						const id = this.data.getId(this._focusIndex);
						this.selection.add(id);
						this.events.fire(DataViewEvents.click, [id, e]);
					},
					"enter+shift": (e: KeyboardEvent) => {
						const id = this.data.getId(this._focusIndex);
						this.selection.add(id, false, true);
						this.events.fire(DataViewEvents.click, [id, e]);
					},
					"enter+ctrl": (e: KeyboardEvent) => {
						const id = this.data.getId(this._focusIndex);
						this.selection.add(id, true, false);
						this.events.fire(DataViewEvents.click, [id, e]);
					}
				};
				if (isIE()) {
					handlers = {
						up: handlers.arrowUp,
						down: handlers.arrowDown,
						right: handlers.arrowRight,
						left: handlers.arrowLeft,
						...handlers
					};
					delete handlers.arrowUp;
					delete handlers.arrowDown;
					delete handlers.arrowRight;
					delete handlers.arrowLeft;
				}
				this._navigationDestructor = addHotkeys(handlers, keyNavigation);
			}
		}
	}
}